import * as React from "react"

import { cn } from "@/shared/ui/cn"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/primitives/select"
import { Input } from "@/shared/ui/primitives/input"
import {
  loadPhoneNumberLib,
  type CountryCode,
  type PhoneNumberLib,
} from "@/shared/ui/primitives/load-phone-number-lib"

type Country = {
  code: CountryCode
  name: string
  flag: string
  dialCode: string
}

const COUNTRIES: readonly Country[] = [
  { code: "US", name: "United States", flag: "🇺🇸", dialCode: "+1" },
  { code: "CA", name: "Canada", flag: "🇨🇦", dialCode: "+1" },
  { code: "BR", name: "Brazil", flag: "🇧🇷", dialCode: "+55" },
  { code: "MX", name: "Mexico", flag: "🇲🇽", dialCode: "+52" },
  { code: "GB", name: "United Kingdom", flag: "🇬🇧", dialCode: "+44" },
  { code: "AU", name: "Australia", flag: "🇦🇺", dialCode: "+61" },
  { code: "DE", name: "Germany", flag: "🇩🇪", dialCode: "+49" },
  { code: "FR", name: "France", flag: "🇫🇷", dialCode: "+33" },
  { code: "IT", name: "Italy", flag: "🇮🇹", dialCode: "+39" },
  { code: "ES", name: "Spain", flag: "🇪🇸", dialCode: "+34" },
  { code: "PT", name: "Portugal", flag: "🇵🇹", dialCode: "+351" },
  { code: "NL", name: "Netherlands", flag: "🇳🇱", dialCode: "+31" },
  { code: "BE", name: "Belgium", flag: "🇧🇪", dialCode: "+32" },
  { code: "CH", name: "Switzerland", flag: "🇨🇭", dialCode: "+41" },
  { code: "AT", name: "Austria", flag: "🇦🇹", dialCode: "+43" },
  { code: "SE", name: "Sweden", flag: "🇸🇪", dialCode: "+46" },
  { code: "NO", name: "Norway", flag: "🇳🇴", dialCode: "+47" },
  { code: "DK", name: "Denmark", flag: "🇩🇰", dialCode: "+45" },
  { code: "FI", name: "Finland", flag: "🇫🇮", dialCode: "+358" },
  { code: "PL", name: "Poland", flag: "🇵🇱", dialCode: "+48" },
  { code: "IE", name: "Ireland", flag: "🇮🇪", dialCode: "+353" },
  { code: "NZ", name: "New Zealand", flag: "🇳🇿", dialCode: "+64" },
  { code: "SG", name: "Singapore", flag: "🇸🇬", dialCode: "+65" },
  { code: "IN", name: "India", flag: "🇮🇳", dialCode: "+91" },
  { code: "JP", name: "Japan", flag: "🇯🇵", dialCode: "+81" },
  { code: "KR", name: "South Korea", flag: "🇰🇷", dialCode: "+82" },
  { code: "CN", name: "China", flag: "🇨🇳", dialCode: "+86" },
  { code: "AR", name: "Argentina", flag: "🇦🇷", dialCode: "+54" },
  { code: "CL", name: "Chile", flag: "🇨🇱", dialCode: "+56" },
  { code: "CO", name: "Colombia", flag: "🇨🇴", dialCode: "+57" },
  { code: "ZA", name: "South Africa", flag: "🇿🇦", dialCode: "+27" },
  { code: "AE", name: "United Arab Emirates", flag: "🇦🇪", dialCode: "+971" },
  { code: "IL", name: "Israel", flag: "🇮🇱", dialCode: "+972" },
  { code: "TR", name: "Turkey", flag: "🇹🇷", dialCode: "+90" },
] as const

const DEFAULT_COUNTRY: Country = COUNTRIES[0]

function dialCodeOf(country: Country): string {
  return country.dialCode
}

function detectCountryFromE164(
  lib: PhoneNumberLib | null,
  value: string
): Country {
  if (!value.startsWith("+")) return DEFAULT_COUNTRY
  if (lib) {
    const parsed = lib.parsePhoneNumberFromString(value)
    if (parsed?.country) {
      const match = COUNTRIES.find((c) => c.code === parsed.country)
      if (match) return match
    }
  }
  const sorted = [...COUNTRIES].sort(
    (a, b) => dialCodeOf(b).length - dialCodeOf(a).length,
  )
  const byDial = sorted.find((c) => value.startsWith(dialCodeOf(c)))
  return byDial ?? DEFAULT_COUNTRY
}

function nationalDigitsFromE164(value: string, country: Country): string {
  const dial = dialCodeOf(country)
  if (value.startsWith(dial)) return value.slice(dial.length).replace(/\D/g, "")
  if (value.startsWith("+")) return value.slice(1).replace(/\D/g, "")
  return value.replace(/\D/g, "")
}

function buildE164(country: Country, digits: string): string {
  if (digits === "") return ""
  return `${dialCodeOf(country)}${digits}`
}

function computeValidity(
  lib: PhoneNumberLib | null,
  country: Country,
  digits: string
): boolean {
  if (digits === "") return false
  if (!lib) return false
  return lib.isValidPhoneNumber(buildE164(country, digits), country.code)
}

function formatNationalNumber(
  lib: PhoneNumberLib | null,
  country: Country,
  digits: string
): string {
  if (digits === "") return ""
  if (!lib) return digits
  return new lib.AsYouType(country.code).input(digits)
}

export type SeparatedPhoneInputProps = {
  value: string
  onChange: (e164: string, valid: boolean) => void
  onBlur?: () => void
  disabled?: boolean
  className?: string
  id?: string
  autoFocus?: boolean
  placeholder?: string
  autoComplete?: string
}

function SeparatedPhoneInput({
  value,
  onChange,
  onBlur,
  disabled,
  className,
  id,
  autoFocus,
  placeholder,
  autoComplete,
}: SeparatedPhoneInputProps) {
  const [country, setCountry] = React.useState<Country>(() =>
    detectCountryFromE164(null, value),
  )
  const [digits, setDigits] = React.useState<string>(() =>
    nationalDigitsFromE164(value, detectCountryFromE164(null, value)),
  )
  const [lib, setLib] = React.useState<PhoneNumberLib | null>(null)

  React.useEffect(() => {
    let cancelled = false
    loadPhoneNumberLib()
      .then((loaded) => {
        if (!cancelled) setLib(loaded)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const handleCountryChange = (code: string) => {
    const next = COUNTRIES.find((c) => c.code === (code as CountryCode)) ?? DEFAULT_COUNTRY
    setCountry(next)
    const e164 = buildE164(next, digits)
    onChange(e164, computeValidity(lib, next, digits))
  }

  const handleDigitsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value
    const cleaned = raw.replace(/\D/g, "")
    const deletedFormattingChar =
      raw.length < formatNationalNumber(lib, country, digits).length &&
      cleaned.length === digits.length
    const candidate = deletedFormattingChar ? cleaned.slice(0, -1) : cleaned
    const tooLong =
      lib !== null &&
      candidate.length > digits.length &&
      lib.validatePhoneNumberLength(buildE164(country, candidate), country.code) ===
        "TOO_LONG"
    const next = tooLong ? digits : candidate
    setDigits(next)
    const e164 = buildE164(country, next)
    onChange(e164, computeValidity(lib, country, next))
  }

  return (
    <div className={cn("flex gap-2", className)}>
      <Select
        value={country.code}
        onValueChange={handleCountryChange}
        disabled={disabled}
      >
        <SelectTrigger className="w-28" aria-label="Country code">
          <SelectValue>
            {country.flag} {dialCodeOf(country)}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {COUNTRIES.map((c) => (
            <SelectItem key={c.code} value={c.code}>
              {c.flag} {c.name} ({dialCodeOf(c)})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        id={id}
        type="tel"
        inputMode="numeric"
        autoComplete={autoComplete ?? "tel"}
        autoFocus={autoFocus}
        placeholder={placeholder}
        disabled={disabled}
        value={formatNationalNumber(lib, country, digits)}
        onChange={handleDigitsChange}
        onBlur={onBlur}
      />
    </div>
  )
}

export { SeparatedPhoneInput }
